import studentTracking from './studentTracking';

// Enhanced Edit Tracking Utility
class EditTracking {
  constructor() {
    this.tracking = studentTracking;
  }

  // Track data field changes with before/after values
  async trackFieldEdit(entityType, entityId, fieldName, oldValue, newValue, context = {}) {
    const changeDetails = {
      entityType,
      entityId,
      fieldName,
      oldValue: this.sanitizeValue(oldValue),
      newValue: this.sanitizeValue(newValue),
      changeType: this.getChangeType(oldValue, newValue),
      ...context
    };

    await this.tracking.trackActivity('field_edit', changeDetails, {
      timestamp: new Date().toISOString(),
      changeSize: this.calculateChangeSize(oldValue, newValue)
    });
  }

  // Track bulk edits (multiple fields at once)
  async trackBulkEdit(entityType, entityId, changes, context = {}) {
    const changeDetails = {
      entityType,
      entityId,
      changes: changes.map(change => ({
        fieldName: change.fieldName,
        oldValue: this.sanitizeValue(change.oldValue),
        newValue: this.sanitizeValue(change.newValue),
        changeType: this.getChangeType(change.oldValue, change.newValue)
      })),
      totalChanges: changes.length,
      ...context
    };

    await this.tracking.trackActivity('bulk_edit', changeDetails, {
      timestamp: new Date().toISOString(),
      changeCount: changes.length
    });
  }

  // Track patient data edits
  async trackPatientEdit(patientId, fieldName, oldValue, newValue, context = {}) {
    await this.trackFieldEdit('patient', patientId, fieldName, oldValue, newValue, {
      patientId,
      ...context
    });
  }

  // Track tooth chart edits
  async trackToothChartEdit(chartId, toothNumber, fieldName, oldValue, newValue, context = {}) {
    await this.trackFieldEdit('tooth_chart', chartId, fieldName, oldValue, newValue, {
      chartId,
      toothNumber,
      ...context
    });
  }

  // Track treatment sheet edits
  async trackSheetEdit(sheetId, sheetType, fieldName, oldValue, newValue, context = {}) {
    await this.trackFieldEdit('treatment_sheet', sheetId, fieldName, oldValue, newValue, {
      sheetId,
      sheetType,
      ...context
    });
  }

  // Track review edits
  async trackReviewEdit(reviewId, fieldName, oldValue, newValue, context = {}) {
    await this.trackFieldEdit('review', reviewId, fieldName, oldValue, newValue, {
      reviewId,
      ...context
    });
  }

  // Track appointment edits
  async trackAppointmentEdit(appointmentId, fieldName, oldValue, newValue, context = {}) {
    await this.trackFieldEdit('appointment', appointmentId, fieldName, oldValue, newValue, {
      appointmentId,
      ...context
    });
  }

  // Track medical record edits
  async trackMedicalRecordEdit(patientId, recordType, fieldName, oldValue, newValue, context = {}) {
    await this.trackFieldEdit('medical_record', patientId, fieldName, oldValue, newValue, {
      patientId,
      recordType,
      ...context
    });
  }

  // Track file uploads/edits
  async trackFileEdit(entityType, entityId, fileName, operation, oldFile = null, newFile = null, context = {}) {
    const changeDetails = {
      entityType,
      entityId,
      fileName,
      operation, // 'upload', 'replace', 'delete', 'rename'
      oldFile: oldFile ? {
        name: oldFile.name,
        size: oldFile.size,
        type: oldFile.type
      } : null,
      newFile: newFile ? {
        name: newFile.name,
        size: newFile.size,
        type: newFile.type
      } : null,
      ...context
    };

    await this.tracking.trackActivity('file_edit', changeDetails, {
      timestamp: new Date().toISOString(),
      fileOperation: operation
    });
  }

  // Track status changes
  async trackStatusChange(entityType, entityId, oldStatus, newStatus, context = {}) {
    await this.trackFieldEdit(entityType, entityId, 'status', oldStatus, newStatus, {
      statusChange: true,
      ...context
    });
  }

  // Track deletion operations
  async trackDeletion(entityType, entityId, entityData, context = {}) {
    const changeDetails = {
      entityType,
      entityId,
      deletedData: this.sanitizeEntityData(entityData),
      ...context
    };

    await this.tracking.trackActivity('entity_deletion', changeDetails, {
      timestamp: new Date().toISOString(),
      deletionType: 'soft' // or 'hard' based on implementation
    });
  }

  // Track creation operations
  async trackCreation(entityType, entityId, entityData, context = {}) {
    const changeDetails = {
      entityType,
      entityId,
      createdData: this.sanitizeEntityData(entityData),
      ...context
    };

    await this.tracking.trackActivity('entity_creation', changeDetails, {
      timestamp: new Date().toISOString()
    });
  }

  // Track form validation errors
  async trackValidationError(formName, fieldName, errorMessage, fieldValue, context = {}) {
    await this.tracking.trackActivity('validation_error', {
      formName,
      fieldName,
      errorMessage,
      fieldValue: this.sanitizeValue(fieldValue),
      ...context
    });
  }

  // Track save operations
  async trackSaveOperation(entityType, entityId, operation, success, errorMessage = null, context = {}) {
    await this.tracking.trackActivity('save_operation', {
      entityType,
      entityId,
      operation, // 'create', 'update', 'delete'
      success,
      errorMessage,
      ...context
    });
  }

  // Helper methods
  sanitizeValue(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string') {
      // Truncate very long strings
      return value.length > 500 ? value.substring(0, 500) + '...' : value;
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  }

  sanitizeEntityData(data) {
    if (!data || typeof data !== 'object') return data;
    
    const sanitized = {};
    for (const [key, value] of Object.entries(data)) {
      sanitized[key] = this.sanitizeValue(value);
    }
    return sanitized;
  }

  getChangeType(oldValue, newValue) {
    if (oldValue === null || oldValue === undefined) return 'added';
    if (newValue === null || newValue === undefined) return 'removed';
    if (oldValue !== newValue) return 'modified';
    return 'unchanged';
  }

  calculateChangeSize(oldValue, newValue) {
    const oldSize = oldValue ? String(oldValue).length : 0;
    const newSize = newValue ? String(newValue).length : 0;
    return Math.abs(newSize - oldSize);
  }

  // Track complex operations like form submissions
  async trackFormSubmission(formName, formData, success, errors = [], context = {}) {
    await this.tracking.trackActivity('form_submission', {
      formName,
      success,
      fieldCount: Object.keys(formData).length,
      errorCount: errors.length,
      errors: errors.map(error => ({
        field: error.field,
        message: error.message
      })),
      ...context
    });
  }

  // Track data export operations
  async trackExport(entityType, entityId, exportFormat, recordCount, context = {}) {
    await this.tracking.trackActivity('data_export', {
      entityType,
      entityId,
      exportFormat,
      recordCount,
      ...context
    });
  }

  // Track data import operations
  async trackImport(entityType, fileName, recordCount, successCount, errorCount, context = {}) {
    await this.tracking.trackActivity('data_import', {
      entityType,
      fileName,
      recordCount,
      successCount,
      errorCount,
      ...context
    });
  }
}

// Create singleton instance
const editTracking = new EditTracking();

export default editTracking;

